import { useLazyQuery, useQuery } from "@apollo/client";
import moment from "moment-timezone";

import {
  DOWNLOAD_PROCEDURE_PDF_BY_ID,
  GET_PROCEDURE_PDF_DOWNLOAD_STATUS,
  GET_OWN_OEM_PROCEDURE_TEMPLATE,
  GET_OWN_OEM_PROCEDURE_INSTANCE,
  LIST_OEM_PROCEDURE_TEMPLATES_QUERY,
  LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY,
  GET_NON_FINALIZED_PROCEDURES_WITH_TEMPLATE,
} from "~/api/_queries";

const PROCEDURE_PDF_POLL_INTERVAL_MS = 1500;
const PROCEDURE_PDF_POLL_TIMEOUT_MS = 3 * 60 * 1000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const useGetAllProcedureTemplates = (params = {}) => {
  const {
    searchQuery = "",
    teamId,
    machineId,
    assetIds = [],
    skip = false,
  } = params;
  const hasAssetIds = assetIds.length > 0;

  const {
    data: { listOwnOemProcedureTemplatesWithCount = {} } = {},
    loading,
    error,
    refetch,
  } = useQuery(LIST_OEM_PROCEDURE_TEMPLATES_QUERY, {
    variables: {
      params: {
        ...(searchQuery || teamId || machineId || hasAssetIds
          ? {
              where: {
                searchQuery,
                teamId,
                machineId,
                ...(hasAssetIds && { assetIds }),
              },
            }
          : {}),
      },
    },
    // Templates can be deleted by other users; always revalidate so a stale
    // cached list doesn't link to procedures that no longer exist (404).
    fetchPolicy: "cache-and-network",
    skip,
  });

  return {
    allProcedures: listOwnOemProcedureTemplatesWithCount.procedures,
    totalCount: listOwnOemProcedureTemplatesWithCount.totalCount,
    // Only report loading when there's nothing cached to show, so the
    // background revalidation doesn't flash a full-page loader.
    fetchingProcedures:
      loading && !listOwnOemProcedureTemplatesWithCount.procedures,
    errorFetchingProcedures: error,
    refetch,
  };
};

export const useGetProcedureTemplateById = (id) => {
  const {
    data: { getOwnOemProcedureTemplate = {} } = {},
    loading,
    error,
    refetch,
  } = useQuery(GET_OWN_OEM_PROCEDURE_TEMPLATE, {
    variables: {
      id,
    },
  });

  return {
    procedure: getOwnOemProcedureTemplate,
    loading,
    error,
    refetch,
  };
};

export const useGetProcedureInstanceById = (id) => {
  const {
    data: { getOwnOemProcedureById = {} } = {},
    loading,
    error,
  } = useQuery(GET_OWN_OEM_PROCEDURE_INSTANCE, {
    variables: {
      id,
    },
    fetchPolicy: "network-only",
  });

  return {
    procedureData: getOwnOemProcedureById,
    loading,
    error,
  };
};

export const useListDetachedProcedures = (
  searchQuery = "",
  { skip = false } = {},
) => {
  const {
    data: { listDetachedProcedures = {} } = {},
    loading,
    error,
    refetch,
  } = useQuery(LIST_OWN_OEM_DETACHED_PROCEDURES_QUERY, {
    skip,
    variables: {
      params: {
        ...(searchQuery ? { where: { searchQuery } } : {}),
      },
    },
    fetchPolicy: "cache-and-network",
  });

  return {
    detachedProcedures: listDetachedProcedures?.procedures,
    totalCount: listDetachedProcedures?.totalCount,
    fetchingDetached: loading && !listDetachedProcedures?.procedures,
    errorFetchingDetached: error,
    refetch,
  };
};

export const useGetProcedurePdfById = () => {
  const [
    download,
    { data: { downloadProcedurePDF = "" } = {}, loading, error },
  ] = useLazyQuery(DOWNLOAD_PROCEDURE_PDF_BY_ID);

  const [getStatus] = useLazyQuery(GET_PROCEDURE_PDF_DOWNLOAD_STATUS);

  const timezone = moment.tz.guess(true);

  const pollProcedurePdfJob = async (jobId) => {
    const startedAt = Date.now();

    while (Date.now() - startedAt < PROCEDURE_PDF_POLL_TIMEOUT_MS) {
      const { data, error: statusError } = await getStatus({
        variables: { jobId },
        fetchPolicy: "network-only",
      });

      if (statusError) {
        return { success: false, error: statusError };
      }

      const statusPayload = data?.getProcedurePDFDownloadStatus;
      const status = statusPayload?.status;

      if (status === "done" && statusPayload?.url) {
        return { success: true, pdfUrl: statusPayload.url, jobId };
      }

      if (status === "failed") {
        return {
          success: false,
          error: statusPayload?.error || true,
          jobId,
        };
      }

      await sleep(PROCEDURE_PDF_POLL_INTERVAL_MS);
    }

    return { success: false, error: "timeout", jobId };
  };

  return {
    downloadingPdf: loading,
    pdfDownloadError: error,
    url: downloadProcedurePDF,
    getProcedurePdf: async (id, uuid) => {
      const response = await download({
        variables: {
          id,
          uuid,
          timezone,
        },
        fetchPolicy: "network-only",
      });

      if (response?.error) {
        return { success: false, error: response.error };
      }

      const jobId = response?.data?.downloadProcedurePDF;
      if (!jobId) {
        return { success: false, error: true };
      }

      return pollProcedurePdfJob(jobId);
    },
  };
};

export const useGetNonFinalizedProceduresCount = () => {
  const [
    getCount,
    {
      data: { getNonFinalizedProceduresWithTemplate = {} } = {},
      loading,
      error,
    },
  ] = useLazyQuery(GET_NON_FINALIZED_PROCEDURES_WITH_TEMPLATE);

  return {
    loading,
    error,
    count: getNonFinalizedProceduresWithTemplate?.count || 0,
    getNonFinalizedCount: (procedureTemplateId) =>
      getCount({
        variables: {
          procedureTemplateId,
        },
        fetchPolicy: "network-only",
      }),
  };
};
